import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MeetingStatus } from '@interfaces/meetings/responses/meeting.response';
import {
  MeetingStatusTone,
  rotuloDaSituacao,
  tomDaSituacao,
} from '@features/dashboard/meetings/components/meeting-presenter';

/**
 * Pílula com a situação da ligação, em português (card 3112).
 *
 * `label`/`tone` trocam o texto e a cor quando a tela precisa de outra leitura da mesma situação
 * (aba Próximas, card 3162: "Agendado / Com bot").
 *
 * Mesmas cores do `.pill` da tela do deal, copiadas em literal de propósito — ver o cabeçalho de
 * `deal-detail.component.scss`: os tokens do design system não existem no tema carregado.
 */
@Component({
  selector: 'app-meeting-status-tag',
  standalone: true,
  template: `
    <span class="meeting-status" [class]="'meeting-status meeting-status--' + tom()">
      <span class="meeting-status__dot" aria-hidden="true"></span>
      {{ rotulo() }}
    </span>
  `,
  styles: `
    .meeting-status {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.2rem 0.6rem;
      border-radius: 999px;
      font-size: 0.72rem;
      font-weight: 600;
      white-space: nowrap;
    }
    .meeting-status__dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
    }
    .meeting-status--success {
      background: rgba(0, 226, 32, 0.16);
      color: #04671a;
    }
    .meeting-status--info {
      background: rgba(33, 181, 234, 0.14);
      color: #0b5f7e;
    }
    .meeting-status--warning {
      background: rgba(255, 206, 0, 0.18);
      color: #7a5c00;
    }
    .meeting-status--danger {
      background: rgba(220, 53, 69, 0.12);
      color: #9b1b26;
    }
    .meeting-status--muted {
      background: #eeeeee;
      color: #555555;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingStatusTagComponent {
  readonly status = input<MeetingStatus | null | undefined>(null);
  readonly label = input<string | null>(null);
  readonly tone = input<MeetingStatusTone | null>(null);

  protected readonly rotulo = computed(() => this.label() ?? rotuloDaSituacao(this.status()));
  protected readonly tom = computed(() => this.tone() ?? tomDaSituacao(this.status()));
}
